import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import Header24 from './components/Header24';
import Footer24 from './components/Footer24';

const Login24 = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!email || !password) {
            setError('Please fill in all fields');
            return;
        }
        navigate('/template24/profile');
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,500&family=Inter:wght@300;400;500;600;700&display=swap');

                .dhara-login {
                    min-height: 100vh;
                    background: #FFFFFF;
                }
                .dhara-login-split {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    min-height: calc(100vh - 80px);
                }
                .dhara-login-image {
                    background-image: url('https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=1200');
                    background-size: cover;
                    background-position: center;
                    position: relative;
                }
                .dhara-login-image::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background: rgba(0, 85, 58, 0.08);
                }
                .dhara-login-form-wrap {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 60px;
                }
                .dhara-login-form {
                    width: 100%;
                    max-width: 400px;
                }
                .dhara-login-welcome {
                    font-family: 'Playfair Display', serif;
                    font-size: 40px;
                    font-weight: 400;
                    color: #1A1A1A;
                    margin-bottom: 12px;
                    letter-spacing: -0.5px;
                }
                .dhara-login-subtitle {
                    font-family: 'Inter', sans-serif;
                    font-size: 15px;
                    color: #6B6B6B;
                    font-weight: 300;
                    margin-bottom: 48px;
                    line-height: 1.6;
                }
                .dhara-login-field {
                    margin-bottom: 24px;
                }
                .dhara-login-label {
                    display: block;
                    font-family: 'Inter', sans-serif;
                    font-size: 12px;
                    font-weight: 500;
                    letter-spacing: 0.5px;
                    color: #6B6B6B;
                    margin-bottom: 8px;
                }
                .dhara-login-input-wrap {
                    position: relative;
                }
                .dhara-login-input {
                    width: 100%;
                    padding: 14px 16px;
                    border: 1px solid #E5E5E5;
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    color: #1A1A1A;
                    background: #FFFFFF;
                    outline: none;
                    transition: border-color 0.3s ease;
                    box-sizing: border-box;
                }
                .dhara-login-input:focus {
                    border-color: #00553A;
                }
                .dhara-login-input::placeholder {
                    color: #BDBDBD;
                }
                .dhara-login-eye {
                    position: absolute;
                    right: 14px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: #6B6B6B;
                }
                .dhara-login-forgot {
                    display: block;
                    text-align: right;
                    font-family: 'Inter', sans-serif;
                    font-size: 13px;
                    color: #6B6B6B;
                    margin-bottom: 32px;
                    cursor: pointer;
                    background: none;
                    border: none;
                    padding: 0;
                    margin-left: auto;
                    transition: color 0.3s ease;
                }
                .dhara-login-forgot:hover {
                    color: #00553A;
                }
                .dhara-login-submit {
                    width: 100%;
                    height: 52px;
                    background: #00553A;
                    color: #FFFFFF;
                    border: none;
                    font-family: 'Inter', sans-serif;
                    font-size: 13px;
                    font-weight: 500;
                    letter-spacing: 1.5px;
                    text-transform: uppercase;
                    cursor: pointer;
                    transition: background 0.3s ease;
                    margin-bottom: 24px;
                }
                .dhara-login-submit:hover {
                    background: #006B4A;
                }
                .dhara-login-signup {
                    text-align: center;
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    color: #6B6B6B;
                }
                .dhara-login-signup-link {
                    color: #00553A;
                    cursor: pointer;
                    font-weight: 500;
                    background: none;
                    border: none;
                    padding: 0;
                    font-family: inherit;
                    font-size: inherit;
                    transition: opacity 0.3s ease;
                }
                .dhara-login-signup-link:hover {
                    opacity: 0.8;
                }
                .dhara-login-error {
                    font-family: 'Inter', sans-serif;
                    font-size: 13px;
                    color: #C53030;
                    margin-bottom: 16px;
                    padding: 12px 16px;
                    background: #FFF5F5;
                    border: 1px solid #FED7D7;
                }

                @media (max-width: 768px) {
                    .dhara-login-split {
                        grid-template-columns: 1fr;
                    }
                    .dhara-login-image {
                        display: none;
                    }
                    .dhara-login-form-wrap {
                        padding: 40px 20px;
                    }
                    .dhara-login-welcome { font-size: 32px; }
                }
            `}</style>

            <Header24 />

            <main className="dhara-login">
                <div className="dhara-login-split">
                    <div className="dhara-login-image" />
                    <div className="dhara-login-form-wrap">
                        <form className="dhara-login-form" onSubmit={handleSubmit}>
                            <h1 className="dhara-login-welcome">Welcome</h1>
                            <p className="dhara-login-subtitle">
                                Sign in to continue your journey with us.
                            </p>

                            {error && <div className="dhara-login-error">{error}</div>}

                            <div className="dhara-login-field">
                                <label className="dhara-login-label">Email</label>
                                <input
                                    className="dhara-login-input"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="your@email.com"
                                />
                            </div>

                            <div className="dhara-login-field">
                                <label className="dhara-login-label">Password</label>
                                <div className="dhara-login-input-wrap">
                                    <input
                                        className="dhara-login-input"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Enter password"
                                    />
                                    <button
                                        type="button"
                                        className="dhara-login-eye"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff size={18} strokeWidth={1.5} /> : <Eye size={18} strokeWidth={1.5} />}
                                    </button>
                                </div>
                            </div>

                            <button type="button" className="dhara-login-forgot">
                                Forgot password?
                            </button>

                            <button type="submit" className="dhara-login-submit">
                                Sign In
                            </button>

                            <div className="dhara-login-signup">
                                New here?{' '}
                                <button
                                    type="button"
                                    className="dhara-login-signup-link"
                                    onClick={() => navigate('/template24/signup')}
                                >
                                    Create an account
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </main>

            <Footer24 />
        </>
    );
};

export default Login24;
